/**
 * Plan of Record — Sponsor Brief.
 *
 * The audience-shaped read view for the executive sponsor: the one-pager to
 * bring to the one-on-one. Condition first, then what needs a decision, how
 * far along the plan is, the primary KPIs, what is blocked (with the blocker
 * named), and the confirmed proof points. Read-only; "the answer is a URL" —
 * this page is the URL: /planning/plan-of-record/:id/sponsor.
 *
 * Deliberately NOT wired into the page-visibility settings system — the
 * brief is always available.
 */

import type { AssembledPor } from '../../types/plan-of-record';
import { ConditionBand } from '../../components/plan-of-record/ConditionBand';
import { PorProgressSummary } from '../../components/plan-of-record/PorProgressSummary';
import { PorNarrativeMarkdown } from '../../components/plan-of-record/PorNarrativeMarkdown';
import {
  ASK_DECISION_TYPE_LABELS,
  AskStatusPill,
  CommitmentStatusPill,
  CopyLinkButton,
  KpiReadout,
  PorCard,
  PorCardHeader,
  PorStatusPill,
  formatDate,
} from '../../components/plan-of-record/porUi';

export default function PorSponsorBrief({ assembled }: { assembled: AssembledPor }) {
  const { record } = assembled;
  const openAsks = assembled.executive_asks
    .filter((a) => a.status === 'open')
    .sort((a, b) => a.rank - b.rank);
  const primaryKpis = assembled.kpis.filter((k) => k.tier === 'primary');
  const blocked = assembled.commitments.filter((c) => c.status === 'blocked');
  const proofPoints = assembled.proof_points
    .filter((p) => p.confirmed)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--text-ter)]">
            Sponsor Brief
          </div>
          <h1 className="text-2xl font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-0.5">
            {record.title}
          </h1>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-xs font-mono text-[var(--text-ter)]">v{record.version}</span>
            <PorStatusPill status={record.status} />
            {record.executive_sponsor_email && (
              <span className="text-xs text-[var(--text-ter)]">
                Sponsor · {record.executive_sponsor_email}
              </span>
            )}
          </div>
        </div>
        <CopyLinkButton className="flex-shrink-0 mt-1" />
      </header>

      {/* ── Signal condition + rationale ────────────────────────────────── */}
      <PorCard>
        <ConditionBand assembled={assembled} readOnly />
      </PorCard>

      {/* ── What needs your decision ────────────────────────────────────── */}
      <PorCard>
        <PorCardHeader title="Needs Your Decision" count={openAsks.length} />
        {openAsks.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            Nothing is waiting on you right now.
          </p>
        ) : (
          <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {openAsks.map((ask) => (
              <div key={ask.id} className="py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono tabular-nums text-[var(--text-ter)]">
                    #{ask.rank}
                  </span>
                  <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {ask.description}
                  </span>
                  <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
                    {ASK_DECISION_TYPE_LABELS[ask.decision_type] ?? ask.decision_type}
                  </span>
                  <AskStatusPill status={ask.status} />
                </div>
                {ask.rationale && (
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
                    {ask.rationale}
                  </p>
                )}
                {ask.due_by && (
                  <p className="text-[11px] text-[var(--text-ter)] mt-1">Due {formatDate(ask.due_by)}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </PorCard>

      {/* ── Progress rollup ─────────────────────────────────────────────── */}
      <PorProgressSummary assembled={assembled} />

      {/* ── Primary KPIs ────────────────────────────────────────────────── */}
      <PorCard>
        <PorCardHeader title="Primary KPIs" count={primaryKpis.length} hint="No data ≠ zero." />
        {primaryKpis.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No primary-tier KPIs on this Plan of Record.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {primaryKpis.map((kpi) => (
              <KpiReadout key={kpi.id} kpi={kpi} />
            ))}
          </div>
        )}
      </PorCard>

      {/* ── Blocked, with the blocker named ─────────────────────────────── */}
      {blocked.length > 0 && (
        <PorCard>
          <PorCardHeader title="Blocked" count={blocked.length} />
          <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {blocked.map((c) => (
              <div key={c.id} className="py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {c.title}
                  </span>
                  <CommitmentStatusPill status={c.status} />
                </div>
                {c.blocker_description && (
                  <div className="mt-1 text-xs text-luminous-coral-800 dark:text-luminous-coral-300">
                    <PorNarrativeMarkdown source={c.blocker_description} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </PorCard>
      )}

      {/* ── Confirmed proof points ──────────────────────────────────────── */}
      {proofPoints.length > 0 && (
        <PorCard>
          <PorCardHeader
            title="Proof Points"
            count={proofPoints.length}
            hint="Confirmed results only."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {proofPoints.map((p) => (
              <div
                key={p.id}
                className="border border-[var(--rule)] dark:border-[var(--rule)] p-4"
              >
                <p className="text-2xl font-semibold tabular-nums text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {p.value_display}
                </p>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5">{p.label}</p>
                {p.context && <p className="text-xs text-[var(--text-ter)] mt-1">{p.context}</p>}
              </div>
            ))}
          </div>
        </PorCard>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4 text-xs text-[var(--text-ter)]">
        <span>Version {record.version}</span>
        <span>Last updated {formatDate(record.last_updated)}</span>
        <span className="ml-auto font-mono">Read-only view</span>
      </footer>
    </div>
  );
}
